import React, { useEffect, useState, useCallback, useRef } from "react";
import { warehousesAPI, uploadAPI } from "../../services/api";
import toast from "react-hot-toast";
import { Upload, Trash2, FileText } from "lucide-react";
import { monthLabel } from "../../utils/month";

interface Invoice {
  _id: string; url: string; publicId: string; resourceType: string; fileName: string; uploadedAt: string;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function InvoicePanel({ warehouseId, month }: { warehouseId: string; month: string }) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await warehousesAPI.getImport(warehouseId, month); setInvoices(res.data.data.invoices || []); }
    catch { toast.error("Lỗi tải hoá đơn"); }
    finally { setLoading(false); }
  }, [warehouseId, month]);

  useEffect(() => { load(); }, [load]);

  const handleFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fileName = `HoaDon_${month}_${Date.now()}`;
        const up = await uploadAPI.invoice(file, fileName);
        await warehousesAPI.addInvoice(warehouseId, month, {
          url: up.data.url, publicId: up.data.publicId, resourceType: up.data.resourceType, fileName: file.name,
        });
      }
      toast.success("Đã tải hoá đơn lên");
      load();
    } catch {
      toast.error("Upload hoá đơn thất bại, thử lại");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const del = async (inv: Invoice) => {
    if (!window.confirm(`Xoá hoá đơn "${inv.fileName || "file"}"?`)) return;
    try { await warehousesAPI.deleteInvoice(warehouseId, month, inv._id); toast.success("Đã xoá"); load(); }
    catch { toast.error("Lỗi xoá"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-zinc-800">{monthLabel(month)}</span>
        <label className="flex items-center gap-1.5 text-sm text-amber-600 font-medium py-1 cursor-pointer touch-manipulation">
          <Upload size={14} /> {uploading ? "Đang tải..." : "Tải hoá đơn"}
          <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple hidden
            disabled={uploading} onChange={handleFilesChange} />
        </label>
      </div>

      {loading ? (
        <p className="text-xs text-zinc-400 text-center py-3">Đang tải...</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-zinc-400 text-center py-6">Chưa có hoá đơn nào cho {monthLabel(month)}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {invoices.map(inv => (
            <div key={inv._id} className="relative bg-white border border-zinc-200 rounded-xl overflow-hidden group">
              <a href={inv.url} target="_blank" rel="noopener noreferrer" className="block">
                {inv.resourceType === "image" ? (
                  <img src={inv.url} alt={inv.fileName} className="w-full h-24 object-cover" />
                ) : (
                  <div className="w-full h-24 flex flex-col items-center justify-center gap-1 bg-zinc-50 text-zinc-400">
                    <FileText size={24} />
                    <span className="text-[10px]">PDF</span>
                  </div>
                )}
              </a>
              <div className="px-2 py-1.5">
                <p className="text-[11px] text-zinc-600 truncate" title={inv.fileName}>{inv.fileName || "Hoá đơn"}</p>
                <p className="text-[10px] text-zinc-400">{formatDate(inv.uploadedAt)}</p>
              </div>
              <button
                onClick={() => del(inv)}
                className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs touch-manipulation"
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
